'use client'

import { ContextMenuContent } from '@radix-ui/react-context-menu'
import {
  Button,
  ContextMenu,
  ContextMenuTrigger,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useSidebar,
  useTableState,
} from '@repo/ui'
import { cn } from '@repo/ui/lib'
import { flexRender } from '@tanstack/react-table'
import { PrefetchKind } from 'next/dist/client/components/router-reducer/router-reducer-types'
import Link from 'next/link'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { ReactNode, useEffect, useState } from 'react'

interface DataTableProps<TData> {
  centeredHeaders?: string[]
  contextMenuContent?: (row: TData) => ReactNode
  data: TData[]
  emptyMessage?: ReactNode
  isLoading?: boolean
  pageCount?: number
  perPage?: number
  rowClickPath?: (row: TData) => string
  sorts?: string[]
}

const DefaultEmptyMessage = () => {
  const pathname = usePathname()

  return (
    <div className={'flex min-h-24 w-full flex-col items-center justify-center gap-4'}>
      <p className={'text-base font-medium text-gray-600'}>
        {pathname.includes('management') ? 'No users yet' : 'No shipments yet'}
      </p>
      <Button asChild className={'text-sm'}>
        <Link className="font-semibold" href={'/quotes/new'}>
          Request a quote
        </Link>
      </Button>
    </div>
  )
}

export const DataTable = <TData,>({
  centeredHeaders = [],
  contextMenuContent,
  data,
  emptyMessage = DefaultEmptyMessage(),
  isLoading = false,
  pageCount = 0,
  perPage = 10,
  rowClickPath,
  sorts = [],
}: DataTableProps<TData>) => {
  const router = useRouter()
  const { columns, setData, setPageCount, sorting, table } = useTableState<TData>()
  const { setOpenMobile } = useSidebar()
  const [_open, setOpen] = useState(false)
  //to manage first render behaviour
  const [firstRender, setFirstRender] = useState(true)

  useEffect(() => {
    setFirstRender(false) //disables after first data will be loaded
    setOpenMobile(false)

    if (data) {
      setData(data)
    }
  }, [data])

  useEffect(() => {
    setPageCount(pageCount)
  }, [pageCount])

  const searchParams = useSearchParams()
  const pathname = usePathname()

  useEffect(() => {
    const params = new URLSearchParams(searchParams)
    if (sorting.length > 0) {
      const sort = sorting[0]
      sorts.forEach(sort => params.delete(`sort[${sort}]`))
      params.set(`sort[${sort.id}]`, sort.desc ? 'desc' : 'asc')
    } else {
      sorts.forEach(sort => params.delete(`sort[${sort}]`))
    }

    if (!firstRender) {
      router.replace(`${pathname}?${params.toString()}`)
    }
  }, [sorting])

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <TableHead
                className={'text-[#212B36] dark:text-[#b6c4d2]'}
                isCenter={centeredHeaders.includes(header.id)}
                isSortable={header.column.getCanSort()}
                key={header.id}
                onClick={header.column.getToggleSortingHandler()}
                sortState={header.column.getIsSorted()}
                style={{
                  maxWidth: `${header.getSize()}px`,
                }}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {/*todo this might not be best solution*/}
        {table.getRowModel().rows?.length || firstRender ? (
          table.getRowModel().rows.map(row => (
            <ContextMenu key={row.id} modal>
              <ContextMenuTrigger asChild>
                <TableRow
                  onMouseEnter={() => {
                    if (rowClickPath) {
                      router.prefetch(rowClickPath(row.original), {
                        kind: PrefetchKind.FULL,
                      })
                    }
                  }}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      className={cn(
                        'w-max',
                        !['origin_popup', 'destination_popup', 'chat', 'actions'].some(key =>
                          cell.id.includes(key),
                        ) && 'cursor-pointer',
                      )}
                      key={cell.id}
                      onClick={() => {
                        if (
                          ![
                            'origin_popup',
                            'destination_popup',
                            'chat',
                            'actions',
                            'notes',
                            'comments',
                          ].some(key => cell.id.includes(key)) &&
                          rowClickPath
                        ) {
                          router.push(rowClickPath(row.original))
                        }
                      }}
                      style={{
                        maxWidth: `${cell.column.getSize()}px`,
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              </ContextMenuTrigger>
              <ContextMenuContent
                className={'z-[10000] rounded-md border bg-white shadow-lg'}
                onClick={() => {
                  setOpen(false)
                }}
              >
                {contextMenuContent ? contextMenuContent(row.original) : null}
              </ContextMenuContent>
            </ContextMenu>
          ))
        ) : (
          <TableRow className={isLoading ? 'hover:bg-transparent' : ''}>
            {isLoading ? (
              columns.map((header, index) => (
                <TableCell key={index}>
                  {Array(perPage)
                    .fill(0)
                    .map((_, cellIndex) => (
                      <div
                        className={
                          'w-fit py-2' +
                          (centeredHeaders.includes(header.id ?? '') ? ' mx-auto' : '')
                        }
                        key={`${index}-${cellIndex}`}
                      >
                        <Skeleton className={'h-6 rounded-lg px-4 text-transparent'} key={index}>
                          {header.header as string}
                        </Skeleton>
                      </div>
                    ))}
                </TableCell>
              ))
            ) : (
              <TableCell className={'py-8'} colSpan={columns.length}>
                {emptyMessage}
              </TableCell>
            )}
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}
